import React, { useState } from 'react';
import {
  MessageSquare, Users, CheckCircle2, Clock, AlertCircle,
  Loader2, Send, RotateCcw,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast }  from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS = {
  sending:   { label: 'Sending',   icon: Loader2,      color: 'text-blue-600 bg-blue-50',   animate: true  },
  sent:      { label: 'Sent',      icon: CheckCircle2, color: 'text-green-600 bg-green-50',  animate: false },
  scheduled: { label: 'Scheduled', icon: Clock,        color: 'text-amber-600 bg-amber-50',  animate: false },
  failed:    { label: 'Failed',    icon: AlertCircle,  color: 'text-red-600 bg-red-50',      animate: false },
};

// ── Auth helper ───────────────────────────────────────────────────────────────

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

// ── SMSCampaignHistoryCard ────────────────────────────────────────────────────

/**
 * SMSCampaignHistoryCard
 *
 * Displays a single campaign's metadata and status.
 * For 'scheduled' campaigns shows a "Send Now" action button.
 */
const SMSCampaignHistoryCard = ({ campaign, userRole }) => {
  const [triggering, setTriggering] = useState(false);

  const s = STATUS[campaign.status] || STATUS.sent;
  const StatusIcon = s.icon;

  const canTrigger = (userRole === 'owner' || userRole === 'admin' || userRole === 'sales')
    && (campaign.status === 'scheduled' || campaign.status === 'failed');

  const createdDate = (() => {
    const raw = campaign.createdAt;
    if (!raw) return '—';
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour:  'numeric', minute: '2-digit',
    });
  })();

  const handleTrigger = async () => {
    if (!window.confirm(`Send "${campaign.name}" now to the resolved audience?`)) return;
    setTriggering(true);
    try {
      const token = await getAuthToken();
      const res = await axios.post(
        `${API}/api/campaigns/${campaign.id}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Sending to ${res.data.contactCount} contacts…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger campaign');
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5
        hover:border-orange-200 hover:shadow-sm transition-all duration-200"
      data-testid="campaign-history-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Icon */}
          <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0 mt-0.5">
            <MessageSquare className="w-4 h-4 text-orange-500" />
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h4 className="font-semibold text-gray-900 text-sm truncate" data-testid="campaign-name">
                {campaign.name}
              </h4>
              {/* Status badge */}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${s.color}`}
                data-testid="campaign-status-badge"
              >
                <StatusIcon className={`w-3 h-3 ${s.animate ? 'animate-spin' : ''}`} />
                {s.label}
              </span>
            </div>

            {/* Audience */}
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
              <Users className="w-3 h-3" />
              {campaign.audienceName || 'Unknown Audience'}
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-4 flex-wrap text-xs">
              {campaign.status === 'sent' && (
                <>
                  <span className="text-green-600 font-medium" data-testid="campaign-sent-count">
                    {(campaign.sentCount ?? 0).toLocaleString()} sent
                  </span>
                  {(campaign.failedCount ?? 0) > 0 && (
                    <span className="text-red-500" data-testid="campaign-failed-count">
                      {campaign.failedCount} failed
                    </span>
                  )}
                  {(campaign.skippedCount ?? 0) > 0 && (
                    <span className="text-gray-400">
                      {campaign.skippedCount} skipped
                    </span>
                  )}
                </>
              )}
              {campaign.status === 'scheduled' && campaign.scheduledAt && (
                <span className="text-amber-600" data-testid="campaign-scheduled-time">
                  Scheduled: {new Date(campaign.scheduledAt).toLocaleString()}
                </span>
              )}
              {campaign.status === 'sending' && (
                <span className="text-blue-600">Processing…</span>
              )}
              <span className="text-gray-400">{createdDate}</span>
            </div>
          </div>
        </div>

        {/* Trigger button for scheduled/failed */}
        {canTrigger && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleTrigger}
            disabled={triggering}
            data-testid="campaign-trigger-send-btn"
            className="shrink-0 h-8 text-xs"
          >
            {triggering
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : campaign.status === 'failed'
                ? <><RotateCcw className="w-3.5 h-3.5 mr-1" />Retry</>
                : <><Send className="w-3.5 h-3.5 mr-1" />Send Now</>
            }
          </Button>
        )}
      </div>
    </div>
  );
};

export default SMSCampaignHistoryCard;
